import { z } from 'zod/v4'
import type {
  OrganizationTaxSettings,
  OrganizationTaxSettingsInput,
  TaxLocationOverride,
} from './organization-tax-settings.types'

const percentage = z
  .string()
  .refine(
    (value) => value === '' || (/^\d+(\.\d{0,4})?$/.test(value) && Number(value) <= 100),
    'Enter a percentage from 0 to 100, with up to four decimal places.'
  )
const policy = z.object({
  calculation_mode: z.enum(['automatic', 'fixed']),
  fixed_tax_rate: percentage,
  delivery_tax_mode: z.enum(['automatic', 'taxable', 'non_taxable']),
})

function requireFixedRate(
  value: { calculation_mode: string; fixed_tax_rate: string },
  context: z.RefinementCtx
) {
  if (value.calculation_mode === 'fixed' && value.fixed_tax_rate === '') {
    context.addIssue({
      code: 'custom',
      path: ['fixed_tax_rate'],
      message: 'Enter a fixed tax rate.',
    })
  }
}

const locationOverride = policy
  .extend({
    location_id: z.number().int().positive(),
    calculation_mode: z.enum(['inherit', 'automatic', 'fixed']),
  })
  .superRefine(requireFixedRate)

export const organizationTaxSettingsSchema = policy
  .extend({
    expected_version: z.number().int().positive(),
    additional_nexus_states: z.array(z.string()),
    customize_by_location: z.boolean(),
    location_overrides: z.array(locationOverride),
    responsibility_acknowledged: z.boolean(),
  })
  .superRefine(requireFixedRate)
  .superRefine((value, context) => {
    if (!value.responsibility_acknowledged) {
      context.addIssue({
        code: 'custom',
        path: ['responsibility_acknowledged'],
        message: 'Check the box to agree to the sales tax terms before saving.',
      })
    }
  })

export type TaxSettingsFormValues = z.infer<typeof organizationTaxSettingsSchema>

type TaxLocationOverrideFormValues = z.infer<typeof locationOverride>

export function taxRatePercentage(rate: string | number | null | undefined): string {
  return rate == null ? '' : String(Number((Number(rate) * 100).toFixed(4)))
}

export function taxSettingsFormValues(settings: OrganizationTaxSettings): TaxSettingsFormValues {
  const savedOverrides = settings.location_overrides ?? []
  const locations = settings.locations ?? []
  const overrideValues = (
    override: Omit<TaxLocationOverride, 'calculation_mode'> & {
      calculation_mode: TaxLocationOverrideFormValues['calculation_mode']
    }
  ): TaxLocationOverrideFormValues => ({
    ...override,
    fixed_tax_rate: taxRatePercentage(override.fixed_tax_rate),
  })
  return {
    expected_version: settings.version,
    calculation_mode: settings.calculation_mode ?? 'automatic',
    fixed_tax_rate: taxRatePercentage(settings.fixed_tax_rate),
    delivery_tax_mode: settings.delivery_tax_mode,
    additional_nexus_states: settings.additional_nexus_states,
    customize_by_location: settings.customize_by_location ?? false,
    responsibility_acknowledged: false,
    location_overrides: [
      ...locations.map((location) =>
        overrideValues(
          savedOverrides.find((override) => override.location_id === location.id) ?? {
            location_id: location.id,
            calculation_mode: 'inherit',
            fixed_tax_rate: null,
            delivery_tax_mode: settings.delivery_tax_mode,
          }
        )
      ),
      ...savedOverrides
        .filter((override) => !locations.some((location) => location.id === override.location_id))
        .map(overrideValues),
    ],
  }
}

export function taxSettingsPayload(values: TaxSettingsFormValues): OrganizationTaxSettingsInput {
  const rate = (value: string) => (value === '' ? null : (Number(value) / 100).toFixed(6))
  // Fixed mode presents the legacy Automatic value as Yes; save that choice explicitly.
  const deliveryMode = (policy: {
    calculation_mode: string
    delivery_tax_mode: TaxSettingsFormValues['delivery_tax_mode']
  }) =>
    policy.calculation_mode === 'fixed' && policy.delivery_tax_mode === 'automatic'
      ? 'taxable'
      : policy.delivery_tax_mode
  return {
    ...values,
    fixed_tax_rate: rate(values.fixed_tax_rate),
    delivery_tax_mode: deliveryMode(values),
    location_overrides: values.location_overrides.flatMap((override) =>
      override.calculation_mode === 'inherit'
        ? []
        : [
            {
              ...override,
              calculation_mode: override.calculation_mode,
              fixed_tax_rate: rate(override.fixed_tax_rate),
              delivery_tax_mode: deliveryMode(override),
            },
          ]
    ),
  }
}
